import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { settingsApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import { FormPageSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const EMPTY_FORM = {
  enabled: false,
  smtpHost: "",
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: "",
  smtpPassword: "",
  fromName: "SG Office Supplies",
  fromEmail: "sales@sgoffice.me",
  quoteNotifications: {
    enabled: true,
    adminEmails: ["sales@sgoffice.me"],
    notifyCustomer: true,
    adminSubject: "New quote request from {{name}}",
    customerSubject: "We received your quote request — SG Office",
  },
  orderNotifications: {
    enabled: true,
    adminEmails: ["sales@sgoffice.me"],
    notifyCustomer: true,
    adminSubject: "New order {{orderNumber}} — SG Office",
    customerSubject: "Order confirmation {{orderNumber}} — SG Office",
  },
  contactNotifications: {
    enabled: true,
    adminEmails: ["sales@sgoffice.me"],
    notifyCustomer: true,
    adminSubject: "New contact enquiry from {{name}}",
    customerSubject: "We received your message — SG Office",
  },
};

function emailsToText(list = []) {
  return (list || []).join("\n");
}

function textToEmails(text = "") {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const AdminEmailSettings = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getEmail();
      const email = res.data.data.email || EMPTY_FORM;
      setConfigured(Boolean(res.data.data.configured));
      setForm({
        ...EMPTY_FORM,
        ...email,
        quoteNotifications: {
          ...EMPTY_FORM.quoteNotifications,
          ...email.quoteNotifications,
        },
        orderNotifications: {
          ...EMPTY_FORM.orderNotifications,
          ...email.orderNotifications,
        },
        contactNotifications: {
          ...EMPTY_FORM.contactNotifications,
          ...email.contactNotifications,
        },
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load email settings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const patchNotification = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.smtpPassword === "********") {
        delete payload.smtpPassword;
      }
      const res = await settingsApi.updateEmail(payload);
      const email = res.data.data.email;
      setConfigured(Boolean(res.data.data.configured));
      setForm((prev) => ({
        ...prev,
        ...email,
        smtpPassword: email.smtpPassword || prev.smtpPassword,
      }));
      toast.success("Email settings saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save email settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) {
      toast.info("Enter an email address for the test");
      return;
    }
    setTesting(true);
    try {
      const res = await settingsApi.sendTestEmail(testEmail.trim());
      toast.success(res.data.message || "Test email sent");
    } catch (error) {
      toast.error(getErrorMessage(error, "Test email failed"));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-page">
        <FormPageSkeleton />
      </div>
    );
  }

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Email notifications</h1>
          <p>
            Configure SMTP and branded notification emails for quote requests,
            orders, and contact enquiries.
          </p>
        </div>
        <span className={`manage-count ${configured ? "" : "manage-count-warn"}`}>
          {configured ? "SMTP ready" : "Not configured"}
        </span>
      </header>

      {!configured && (
        <Alert variant="warning">
          Email sending is disabled until SMTP is configured and enabled below.
        </Alert>
      )}

      <Form onSubmit={handleSave} className="manage-card manage-settings-form">
        <h2>SMTP server</h2>
        <Form.Check
          type="switch"
          id="email-enabled"
          className="mb-3"
          label="Enable email sending"
          checked={form.enabled}
          onChange={(e) => patch("enabled", e.target.checked)}
        />

        <Row>
          <Col md={8}>
            <Form.Group className="mb-3">
              <Form.Label>SMTP host</Form.Label>
              <Form.Control
                value={form.smtpHost}
                onChange={(e) => patch("smtpHost", e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Port</Form.Label>
              <Form.Control
                type="number"
                value={form.smtpPort}
                onChange={(e) => patch("smtpPort", Number(e.target.value))}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Check
          type="checkbox"
          id="smtp-secure"
          className="mb-3"
          label="Use SSL/TLS (secure)"
          checked={form.smtpSecure}
          onChange={(e) => patch("smtpSecure", e.target.checked)}
        />

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>SMTP username</Form.Label>
              <Form.Control
                value={form.smtpUser}
                onChange={(e) => patch("smtpUser", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>SMTP password</Form.Label>
              <Form.Control
                type="password"
                value={form.smtpPassword}
                onChange={(e) => patch("smtpPassword", e.target.value)}
                placeholder={form.smtpPassword === "********" ? "Saved" : ""}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>From name</Form.Label>
              <Form.Control
                value={form.fromName}
                onChange={(e) => patch("fromName", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>From email</Form.Label>
              <Form.Control
                type="email"
                value={form.fromEmail}
                onChange={(e) => patch("fromEmail", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <hr />

        <h2>Quote request emails</h2>
        <Form.Check
          type="switch"
          className="mb-3"
          label="Send quote notifications"
          checked={form.quoteNotifications.enabled}
          onChange={(e) => patchNotification("quoteNotifications", "enabled", e.target.checked)}
        />
        <Form.Group className="mb-3">
          <Form.Label>Admin recipients (one email per line)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={emailsToText(form.quoteNotifications.adminEmails)}
            onChange={(e) =>
              patchNotification("quoteNotifications", "adminEmails", textToEmails(e.target.value))
            }
          />
        </Form.Group>
        <Form.Check
          type="checkbox"
          className="mb-3"
          label="Send confirmation email to customer"
          checked={form.quoteNotifications.notifyCustomer}
          onChange={(e) =>
            patchNotification("quoteNotifications", "notifyCustomer", e.target.checked)
          }
        />
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Admin subject</Form.Label>
              <Form.Control
                value={form.quoteNotifications.adminSubject}
                onChange={(e) =>
                  patchNotification("quoteNotifications", "adminSubject", e.target.value)
                }
              />
              <Form.Text>Use {"{{name}}"}, {"{{quoteNumber}}"}, {"{{category}}"}</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Customer subject</Form.Label>
              <Form.Control
                value={form.quoteNotifications.customerSubject}
                onChange={(e) =>
                  patchNotification("quoteNotifications", "customerSubject", e.target.value)
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <hr />

        <h2>Order emails</h2>
        <Form.Check
          type="switch"
          className="mb-3"
          label="Send order notifications"
          checked={form.orderNotifications.enabled}
          onChange={(e) => patchNotification("orderNotifications", "enabled", e.target.checked)}
        />
        <Form.Group className="mb-3">
          <Form.Label>Admin recipients (one email per line)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={emailsToText(form.orderNotifications.adminEmails)}
            onChange={(e) =>
              patchNotification("orderNotifications", "adminEmails", textToEmails(e.target.value))
            }
          />
        </Form.Group>
        <Form.Check
          type="checkbox"
          className="mb-3"
          label="Send confirmation email to customer"
          checked={form.orderNotifications.notifyCustomer}
          onChange={(e) =>
            patchNotification("orderNotifications", "notifyCustomer", e.target.checked)
          }
        />
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Admin subject</Form.Label>
              <Form.Control
                value={form.orderNotifications.adminSubject}
                onChange={(e) =>
                  patchNotification("orderNotifications", "adminSubject", e.target.value)
                }
              />
              <Form.Text>Use {"{{orderNumber}}"}, {"{{name}}"}, {"{{email}}"}</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Customer subject</Form.Label>
              <Form.Control
                value={form.orderNotifications.customerSubject}
                onChange={(e) =>
                  patchNotification("orderNotifications", "customerSubject", e.target.value)
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <hr />

        <h2>Contact enquiry emails</h2>
        <Form.Check
          type="switch"
          className="mb-3"
          label="Send contact enquiry notifications"
          checked={form.contactNotifications.enabled}
          onChange={(e) =>
            patchNotification("contactNotifications", "enabled", e.target.checked)
          }
        />
        <Form.Group className="mb-3">
          <Form.Label>Admin recipients (one email per line)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={emailsToText(form.contactNotifications.adminEmails)}
            onChange={(e) =>
              patchNotification(
                "contactNotifications",
                "adminEmails",
                textToEmails(e.target.value)
              )
            }
          />
        </Form.Group>
        <Form.Check
          type="checkbox"
          className="mb-3"
          label="Send confirmation email to customer"
          checked={form.contactNotifications.notifyCustomer}
          onChange={(e) =>
            patchNotification("contactNotifications", "notifyCustomer", e.target.checked)
          }
        />
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Admin subject</Form.Label>
              <Form.Control
                value={form.contactNotifications.adminSubject}
                onChange={(e) =>
                  patchNotification(
                    "contactNotifications",
                    "adminSubject",
                    e.target.value
                  )
                }
              />
              <Form.Text>Use {"{{name}}"}, {"{{email}}"}, {"{{phone}}"}</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Customer subject</Form.Label>
              <Form.Control
                value={form.contactNotifications.customerSubject}
                onChange={(e) =>
                  patchNotification(
                    "contactNotifications",
                    "customerSubject",
                    e.target.value
                  )
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="manage-settings-actions">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </div>
      </Form>

      <div className="manage-card manage-settings-form mt-3">
        <h2>Send test email</h2>
        <Row className="align-items-end">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Recipient</Form.Label>
              <Form.Control
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Button variant="outline-primary" disabled={testing} onClick={handleTest}>
              {testing ? "Sending..." : "Send test"}
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AdminEmailSettings;
